import CheckIcon from "@mui/icons-material/CheckRounded";
import CloseIcon from "@mui/icons-material/CloseRounded";
import ReplayIcon from "@mui/icons-material/ReplayRounded";
import { Button, Stack } from "@mui/material";
import { useCallback, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import NoAccessPlaceholder from "@entrilia/shared/components/NoAccessPlaceholder";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import { logError } from "@entrilia/shared/logging";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import { getReferrerPath } from "../../../../utilities/navigationHelper";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import FullHeightFlexPage from "../../../common/layout/FullHeightFlexPage";
import ScrollableColumn from "../../../common/layout/ScrollableColumn";
import ScrollableColumns from "../../../common/layout/ScrollableColumns";
import StretchedScrollableContent from "../../../common/layout/StretchedScrollableContent";
import { NotesContextProvider } from "../../../notes/NotesContextProvider";
import { isV2ChangeRequest } from "../changeRequestsFormatters";
import ChangeRequestsNotesSection from "../ChangeRequestsNotesSection";
import ApprovalConfirmationDialog from "./ApprovalConfirmationDialog";
import ChangeRequestDetailsSection from "./ChangeRequestDetailsSection";
import ChangeRequestDetailsTitle from "./ChangeRequestDetailsTitle";
import RejectionConfirmationDialog from "./RejectionConfirmationDialog";
import RetryConfirmationDialog from "./RetryConfirmationDialog";

interface Props {
  backButtonPath: string;
}

type OpenDialog = "approve" | "reject" | "retry";

const ChangeRequestDetailsPage = ({ backButtonPath }: Props) => {
  usePageTitle("Request");

  const { id: workflowId } = useParams();
  const { hasAnyPermission, updateMenuBadges } = useClientContext();
  const navigate = useNavigate();
  const location = useLocation();
  const [openDialog, setOpenDialog] = useState<OpenDialog>();

  const fetchDetails = useCallback(() => adminApi.getInvestorChangeRequestDetails(defined(workflowId)), [workflowId]);

  const [changeRequestDetails, fetchError, { fetch: refetchDetails }] = useFetch(fetchDetails);

  if (fetchError) {
    if (fetchError.code === 403) {
      return <NoAccessPlaceholder text="You do not have permission to view this request." />;
    }

    logError(fetchError, "[ChangeRequestDetailsPage] getInvestorChangeRequestDetails");
    return <DataLoadingFailed title="Loading change request details failed" />;
  }

  if (changeRequestDetails === undefined) {
    return <InlineLoader />;
  }

  const backPath = getReferrerPath(location.state) ?? backButtonPath;

  const handleStatusChange = () => {
    updateMenuBadges();
    navigate(backPath);
  };

  const hasPermissionsToViewNotes = hasAnyPermission(["ViewInvestorRequestsNotes", "ManageInvestorRequestsNotes"]);
  const hasPermissionsToManageNotes = hasAnyPermission(["ManageInvestorRequestsNotes"]);

  const showApprovalControls =
    changeRequestDetails.workflowStatus === "InProgress" &&
    changeRequestDetails.canCurrentUserApprove &&
    !isV2ChangeRequest(changeRequestDetails);

  const showRetryControl =
    changeRequestDetails.workflowStatus === "Failed" &&
    changeRequestDetails.workflowType !== "MembersChangeRequest" &&
    hasAnyPermission(["ManageFundStructure"]);

  return (
    <FullHeightFlexPage>
      <GeneralPageHeader
        TitleComponent={<ChangeRequestDetailsTitle changeRequestDetails={changeRequestDetails} />}
        showDefaultBackButtonTo={backPath}
      >
        {showApprovalControls && (
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Button
              variant="outlined"
              color="success"
              startIcon={<CheckIcon />}
              onClick={() => setOpenDialog("approve")}
            >
              Approve Request
            </Button>
            <Button variant="outlined" color="error" startIcon={<CloseIcon />} onClick={() => setOpenDialog("reject")}>
              Reject
            </Button>
          </Stack>
        )}
        {showRetryControl && (
          <Button variant="outlined" startIcon={<ReplayIcon />} onClick={() => setOpenDialog("retry")}>
            Retry
          </Button>
        )}
      </GeneralPageHeader>
      <StretchedScrollableContent>
        <ScrollableColumns gridProps={{ columnSpacing: 2.5 }}>
          <ScrollableColumn>
            <ChangeRequestDetailsSection
              changeRequestDetails={changeRequestDetails}
              onApprovalActionExecuted={handleStatusChange}
            />
          </ScrollableColumn>
          {hasPermissionsToViewNotes && (
            <ScrollableColumn width="36rem">
              <NotesContextProvider
                entityType="ChangeRequest"
                entityId={defined(workflowId)}
                hasPermissionsToManageNotes={hasPermissionsToManageNotes}
              >
                <ChangeRequestsNotesSection />
              </NotesContextProvider>
            </ScrollableColumn>
          )}
        </ScrollableColumns>
      </StretchedScrollableContent>
      <ApprovalConfirmationDialog
        workflowId={changeRequestDetails.workflowId}
        approvalId={changeRequestDetails.approvalId}
        open={openDialog === "approve"}
        onClose={() => setOpenDialog(undefined)}
        onApproved={handleStatusChange}
      />
      <RejectionConfirmationDialog
        workflowId={changeRequestDetails.workflowId}
        approvalId={changeRequestDetails.approvalId}
        open={openDialog === "reject"}
        onClose={() => setOpenDialog(undefined)}
        onRejected={handleStatusChange}
      />
      <RetryConfirmationDialog
        workflowId={changeRequestDetails.workflowId}
        open={openDialog === "retry"}
        onClose={() => setOpenDialog(undefined)}
        onRetried={refetchDetails}
      />
    </FullHeightFlexPage>
  );
};

export default ChangeRequestDetailsPage;
